/**
 * MarketingSectionSponsors.tsx - Sponsor Banner Configuration Section
 *
 * This component manages sponsor banner configuration for fundraisers
 * including image upload, editing, and display settings.
 *
 * Key Features:
 * - Sponsor banner image upload and management
 * - Image editing and cropping capabilities
 * - Banner preview and positioning options
 * - Integration with media editor for image modifications
 * - Display settings and visibility controls
 * - Sponsor attribution and linking options
 *
 * Banner Configuration:
 * - Image Upload: Drag-and-drop or click to upload
 * - Image Editing: Crop, resize, and adjust banner images
 * - Display Position: Configure where banner appears
 * - Visibility Settings: Control when banner is shown
 *
 * Image Management:
 * - Format validation (JPG, PNG, GIF, WebP)
 * - Size optimization and compression
 * - Aspect ratio recommendations
 * - Preview in context of fundraiser layout
 *
 * Sponsor Features:
 * - Sponsor attribution text and links
 * - Click-through tracking and analytics
 * - Multiple sponsor support
 * - Rotation and scheduling options
 *
 * Props: None (uses MobX store for state management)
 *
 * Dependencies: EditMediaModal, image upload utilities, MobX store
 */

import React, { useState } from "react";
import styled from "styled-components";
import { observer } from "mobx-react-lite";
import { Plus, X, Edit2 } from "lucide-react";
import { EditMediaModal } from "../../media-editor/EditMediaModal";
import { CreateFundraiserModel, MediaModel } from "../../../models";

const CompactBlock = styled.div`
  margin-bottom: 24px;
`;

const BlockTitle = styled.h4`
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 16px;
`;

const FormGroup = styled.div<{ minHeight?: number }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: ${({ minHeight }) => (minHeight ? `${minHeight}px` : "unset")};
`;

const BannerUploadArea = styled.div`
  border: 2px dashed #cbd5e1;
  border-radius: 16px;
  padding: 32px;
  text-align: center;
  background: #ffffff;
  transition: all 0.2s ease;
  cursor: pointer;
  width: 600px;
  height: 125px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;

  &:hover {
    border-color: #3b82f6;
    color: #3b82f6;
    background: #f8fafc;
  }
`;

const BannerImageContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 170px;
  width: auto;
  max-width: 400px;
  min-width: 200px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  padding: 20px;
  cursor: pointer;
`;

const BannerImagePreview = styled.img`
  width: auto;
  height: auto;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 6px;
  cursor: pointer;
`;

const BannerImageDeleteButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: none;
  background: rgba(0, 0, 0, 0.6);
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.8);
  }
`;

const EditIconButton = styled.button`
  position: absolute;
  top: 50%;
  right: 10px;
  transform: translateY(-50%);
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: #3b82f6;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  box-shadow: none;
  z-index: 10;

  &:hover {
    background: rgba(59, 130, 246, 0.1);
    transform: translateY(-50%) scale(1.05);
    box-shadow: none;
  }

  &:active {
    transform: translateY(-50%) scale(0.95);
  }
`;
const UploadIcon = styled.div`
  color: #64748b;
`;

const UploadText = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #374151;
`;

const UploadSubtext = styled.div`
  font-size: 14px;
  color: #64748b;
`;

interface MarketingSectionSponsorsProps {
  builderData: CreateFundraiserModel;
  updateBuilderField: (field: keyof CreateFundraiserModel, value: any) => void;
  onKeyDown?: (e: React.KeyboardEvent, isLastField: boolean) => void;
}

export const MarketingSectionSponsors: React.FC<MarketingSectionSponsorsProps> =
  observer(({ builderData, updateBuilderField, onKeyDown }) => {
    const [showEditMediaModal, setShowEditMediaModal] = useState(false);
    const [editingMediaItem, setEditingMediaItem] = useState<MediaModel | null>(
      null
    );

    const handleBannerImageUpload = (files: FileList | null) => {
      if (!files || files.length === 0) return;

      const file = files[0];
      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onload = (event) => {
          const result = event.target?.result as string;
          updateBuilderField("bannerImage", result);
        };
        reader.readAsDataURL(file);
      }
    };

    const handleRemoveBannerImage = (e: React.MouseEvent) => {
      e.stopPropagation();
      updateBuilderField("bannerImage", null);
    };

    const handleUploadAreaClick = () => {
      updateBuilderField("showMarketingSectionLibrary", true);
    };

    const handleEditBannerClick = () => {
      if (builderData.bannerImage) {
        const mediaItem = new MediaModel({ ...builderData.bannerImage });
        setEditingMediaItem(mediaItem);
        setShowEditMediaModal(true);
      }
    };

    const handleEditMediaSave = (updatedMedia: MediaModel) => {
      updateBuilderField("bannerImage", new MediaModel({ ...updatedMedia }));
      setShowEditMediaModal(false);
      setEditingMediaItem(null);
    };
    return (
      <>
        <CompactBlock>
          <BlockTitle>Sponsors</BlockTitle>
          <div
            style={{ fontSize: "14px", color: "#64748b", marginBottom: "24px" }}
          >
            Include ads on your fundraiser to support your sponsors
          </div>
          <FormGroup minHeight={160}>
            {builderData.bannerImage.fullURL ? (
              <div
                style={{ display: "flex", alignItems: "center", gap: "12px" }}
              >
                <BannerImageContainer onClick={handleUploadAreaClick}>
                  <BannerImagePreview
                    src={builderData.bannerImage.fullURL}
                    alt="Banner"
                  />
                  <BannerImageDeleteButton
                    onClick={handleRemoveBannerImage}
                    type="button"
                  >
                    <X size={12} />
                  </BannerImageDeleteButton>
                </BannerImageContainer>
                <EditIconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditBannerClick();
                  }}
                  type="button"
                  title="Edit banner"
                  style={{
                    position: "static",
                    width: "32px",
                    height: "32px",
                    marginLeft: "15px",
                    transform: "none",
                  }}
                >
                  <Edit2 size={16} />
                </EditIconButton>
              </div>
            ) : (
              <BannerUploadArea onClick={handleUploadAreaClick}>
                <UploadIcon>
                  <Plus size={32} />
                </UploadIcon>
                <UploadText>Add Image</UploadText>
                <UploadSubtext>
                  Select or upload a banner image (recommended 150 x 1000
                  pixels)
                </UploadSubtext>
              </BannerUploadArea>
            )}

            {/* <input
              id="banner-image-input"
              type="file"
              accept="image/*"
              onChange={(e) => handleBannerImageUpload(e.target.files)}
              style={{ display: "none" }}
              onKeyDown={(e) => {
                if (onKeyDown) {
                  onKeyDown(e, true);
                }
              }}
            /> */}
          </FormGroup>
        </CompactBlock>

        {showEditMediaModal && editingMediaItem?.fullURL && (
          <EditMediaModal
            isVisible={showEditMediaModal}
            mediaItem={editingMediaItem}
            onClose={() => {
              setShowEditMediaModal(false);
              setEditingMediaItem(null);
            }}
            onSave={handleEditMediaSave}
            showDetailsPanel={false}
          />
        )}
      </>
    );
  });
